import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Profile } from "../core/models/entities/Profile";
import { useProfileRepository } from "../data/repositories/ProfileRepository";
import { useOrganizationImpersonationContext } from "./OrganizationImpersonationContext";
import { Role } from "../core/models/entities/Role";
import { OrderDeliveryAddress } from "../core/models/entities/Order";
import { useOrganizationRepository } from "../data/repositories/OrganizationRepository";
import { useMsal } from "@azure/msal-react";

import { Organization } from "../core/models/entities/Organization";
import { enviroment } from "../enviroment";
import LoadingElement from "../presentation/components/loading-element/loading-element";

interface ProfileProps {
  origin?: Profile;
  profile?: Profile;
  organization?: Organization;
  addresses: OrderDeliveryAddress[];
  defaultAddress?: OrderDeliveryAddress;
  currency: string;
  isImpersonatingAdmin: boolean;
  // True when the logged-in user has the per-user hide-prices flag
  // ("workshop mode"): no prices, no commissions, anywhere.
  workshopMode: boolean;
  role: Role | undefined;
  b2CId: string | undefined;
  organizationId: number | undefined;
  refetch: () => Promise<Profile>;
  selectDefaultAddress: (addressId: number) => Promise<void>;
  updateProfile: (profile: Profile) => Promise<Profile>;
}

const ProfileContext = createContext<ProfileProps | undefined>(undefined);

interface AuthenticationProviderProps {
  children: React.ReactNode;
}

function ProfileProvider({
  children,
}: AuthenticationProviderProps): React.ReactElement {
  const { organizationId, setVendor } = useOrganizationImpersonationContext();
  // Admin perf FE-2 (flag adminPerfNavMemoEnabled): `navigate` is never
  // called here (it only sits in an effect's deps), but useNavigate()
  // subscribes the provider to every location change (react-router 6.9:
  // useNavigate -> useLocation) = a new context value for ~100 consumers per
  // route change. With the flag on the hook is not called. Build-time
  // constant, so the hook order is stable within a session.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const navigate = enviroment.adminPerfNavMemoEnabled ? undefined : useNavigate();
  const {
    useProfile,
    useAddresses,
    setAddessAsDefault,
    updateProfile,
    updateProfileById,
  } = useProfileRepository();
  const { data, refetch, isError: isProfileError } = useProfile(organizationId);
  const profileOrgId = data?.userOrganizationInformations?.organizationId;
  const { data: addresses, refetch: refetchAddresses } =
    useAddresses(profileOrgId);
  const isAdminRole = data?.userOrganizationInformations.role === Role.Admin;
  const b2CId = data?.userOrganizationInformations.b2CId;
  const [isAdmin, setIsAdmin] = useState<boolean>(isAdminRole);

  // Organization repository for checking disabled status
  const { useOrganization } = useOrganizationRepository();
  const { data: organization } = useOrganization(profileOrgId || 0);
  const { instance } = useMsal();

  useEffect(() => {
    if (isAdminRole) {
      setIsAdmin(true);
    } else {
      setIsAdmin((v) => (v ? true : v));
    }
  }, [isAdminRole]);

  // Refetch only when the impersonation target actually changes. On first
  // mount the profile query has just fetched (login always arrives via a full
  // B2C redirect with a fresh cache), so the old unconditional refetch only
  // duplicated GetProfile/GetDeliveryAddresses on every login.
  const prevOrganizationId = useRef(organizationId);
  useEffect(() => {
    // The prev-value guard (not the deps array) decides whether to refetch,
    // so extra effect runs from changing function identities are harmless.
    if (prevOrganizationId.current === organizationId) return;
    prevOrganizationId.current = organizationId;
    refetch();
    refetchAddresses();
  }, [organizationId, refetch, refetchAddresses]);

  // Check if organization is disabled and redirect to error page.
  // Act-as exception (flag actAsDisabledOrderEnabled): under impersonation the
  // effective profile org IS the act-as target, so this gate used to force-log
  // the ADMIN out the moment a disabled customer/vendor was selected — making
  // it impossible to place an order on a disabled customer's behalf. With the
  // flag on, the forced logout only fires when the disabled org is the user's
  // OWN login org (organizationId == null = no impersonation active). Safe
  // scope: impersonation state can only be held by admins (/profile/{orgId} is
  // admin-only, and the fetch-error effect below clears stale non-admin
  // state). Deliberately NOT based on isImpersonatingAdmin — that latch is
  // false after a hard reload under act-as (see ImpersonationBanner).
  useEffect(() => {
    const actAsBypass =
      enviroment.actAsDisabledOrderEnabled && organizationId != null;
    if (data && organization && organization.disabled && !actAsBypass) {
      // Organization is disabled, logout the user and redirect to error page
      instance.logout({
        postLogoutRedirectUri: `${window.location.origin}/organization-disabled`,
      });
    }
  }, [data, organization, instance, navigate, organizationId]);

  // Act-as-vendor: if the impersonated profile fetch FAILS (e.g. stale
  // sessionStorage restored for a non-admin caller — /profile/{orgId} is
  // admin-only), drop the impersonation instead of leaving the app on an
  // eternal splash. Falls back to the caller's own (warm) profile.
  useEffect(() => {
    if (organizationId != null && isProfileError) {
      setVendor(undefined);
    }
  }, [organizationId, isProfileError, setVendor]);

  async function handleUpdateProfile(profile: Profile): Promise<Profile> {
    if (organizationId) {
      return await updateProfileById(organizationId!, profile);
    } else {
      return await updateProfile(profile);
    }
  }

  const selectDefaultAddress = async (addressId: number) => {
    if (!profileOrgId) return;
    await setAddessAsDefault(profileOrgId, addressId);
    await refetchAddresses();
  };

  // Admin perf FE-2 (flag adminPerfNavMemoEnabled): stable function
  // identities (thin wrappers calling THIS render's implementation via a
  // ref - never stale) + one value object per (data, organization,
  // addresses, isAdmin) tuple, so the ~100 useProfileContext consumers only
  // re-render when the profile really changed. Flag off = the literal object
  // per render as before (hooks are called either way, and BEFORE the act-as
  // render gate below, so the hook order is stable).
  const latestRef = useRef({ handleUpdateProfile, selectDefaultAddress, refetch });
  latestRef.current = { handleUpdateProfile, selectDefaultAddress, refetch };
  const stableRefetch = useCallback(
    () => latestRef.current.refetch().then((x) => x.data!),
    []
  );
  const stableUpdateProfile = useCallback(
    (profile: Profile) => latestRef.current.handleUpdateProfile(profile),
    []
  );
  const stableSelectDefaultAddress = useCallback(
    (addressId: number) => latestRef.current.selectDefaultAddress(addressId),
    []
  );
  const memoValue = useMemo<ProfileProps>(
    () => ({
      profile: data!,
      organization: organization,
      addresses: addresses ?? [],
      defaultAddress: addresses?.find((a) => a.isDefault),
      currency:
        data?.userOrganizationInformations?.organizationCurrency ?? "DKK",
      isImpersonatingAdmin: isAdmin,
      workshopMode: data?.userOrganizationInformations?.hidePrices === true,
      role: data?.userOrganizationInformations.role,
      organizationId: data?.userOrganizationInformations?.organizationId,
      b2CId: b2CId,
      refetch: stableRefetch,
      updateProfile: stableUpdateProfile,
      selectDefaultAddress: stableSelectDefaultAddress,
    }),
    [
      data,
      organization,
      addresses,
      isAdmin,
      b2CId,
      stableRefetch,
      stableUpdateProfile,
      stableSelectDefaultAddress,
    ]
  );

  // Act-as-vendor render gate: while an impersonation target is set but its
  // profile hasn't loaded yet (dialog submit switches the react-query key; a
  // reload with restored sessionStorage starts on the cold ["profile", orgId]
  // key), children must NOT render — downstream consumers dereference profile
  // without guards (Brand's profile.userOrganizationInformations, LoggedInMenu's
  // role switch that falls through to logout()). It also keeps CartProvider from
  // mounting with an undefined b2CId, which would read the wrong localStorage
  // bucket and silently empty the cart. Normal boot (no impersonation) and the
  // feature-off path are untouched.
  if (enviroment.actAsVendorEnabled && organizationId != null && !data) {
    return (
      <div className="h-screen w-screen">
        <LoadingElement />
      </div>
    );
  }

  return (
    <ProfileContext.Provider
      value={
        enviroment.adminPerfNavMemoEnabled
          ? memoValue
          : {
              profile: data!,
              organization: organization,
              addresses: addresses ?? [],
              defaultAddress: addresses?.find((a) => a.isDefault),
              currency:
                data?.userOrganizationInformations?.organizationCurrency ??
                "DKK",
              isImpersonatingAdmin: isAdmin,
              workshopMode:
                data?.userOrganizationInformations?.hidePrices === true,
              role: data?.userOrganizationInformations.role,
              organizationId:
                data?.userOrganizationInformations?.organizationId,
              b2CId: b2CId,
              refetch: () => refetch().then((x) => x.data!),
              updateProfile: handleUpdateProfile,
              selectDefaultAddress,
            }
      }
    >
      {children}
    </ProfileContext.Provider>
  );
}

function useProfileContext() {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error("useProfileContext must be used within a ProfileProvider");
  }

  return context;
}

export { ProfileProvider, useProfileContext, ProfileContext };
